import { useState, type JSX, type ReactNode } from 'react';
import { resolveAssetUrl } from '../assets/overrides';
import { AssetsView } from './AssetsView';
import { DoneCounter } from './DoneCounter';
import { LeaderboardView } from './LeaderboardView';
import { SettingsView } from './SettingsView';
import { SpriteAnimatorView } from './SpriteAnimatorView';
import { TicketsView } from './TicketsView';
import { isFirebaseConfigured } from './firebase';
import { useAuth } from './useAuth';

type Tab = 'assets' | 'tickets' | 'sprites' | 'leaderboard' | 'settings';

// Root of the designer portal. Gates on Firebase config + Google sign-in +
// the designers allowlist, then shows the Assets / Tickets tabs. The tab bodies
// are filled in by later phases (asset browser, tickets board).
export function App(): JSX.Element {
  if (!isFirebaseConfigured()) return <SetupNotice />;
  return <Gate />;
}

function Gate(): JSX.Element {
  const { loading, user, authorized, isAdmin, signIn, signOutNow } = useAuth();
  const [tab, setTab] = useState<Tab>('tickets');

  if (loading) return <Centered>Loading...</Centered>;

  if (!user) {
    return (
      <Centered>
        <div className="login-box">
          <img
            className="login-logo"
            src={resolveAssetUrl('/studio/ss-games-logo.png')}
            alt="Saltshaker Games"
          />
          <button className="primary" onClick={signIn}>
            Sign in with Google
          </button>
        </div>
      </Centered>
    );
  }

  if (!authorized) {
    return (
      <Centered>
        <h1>Not authorized</h1>
        <p>
          {user.email} is not on the designer allowlist. Ask an admin to add a
          designers/{'{email}'} doc.
        </p>
        <button onClick={signOutNow}>Sign out</button>
      </Centered>
    );
  }

  return (
    <div className="studio">
      <header className="topbar">
        <span className="topbar-brand">
          <img className="topbar-logo" src="/ss-logo.png" alt="Saltshaker Games" />
          <strong>Saltshaker Games</strong>
        </span>
        <nav className="tabs">
          <button className={tab === 'assets' ? 'active' : ''} onClick={() => setTab('assets')}>
            <i className="fas fa-images" /> Assets
          </button>
          <button className={tab === 'tickets' ? 'active' : ''} onClick={() => setTab('tickets')}>
            <i className="fas fa-clipboard-list" /> Tickets
          </button>
          <button className={tab === 'sprites' ? 'active' : ''} onClick={() => setTab('sprites')}>
            <i className="fas fa-film" /> Animator
          </button>
          <a className="tab-link" href="/game" title="Open the game">
            <i className="fas fa-play" /> Game
          </a>
        </nav>
        <span className="who">
          <DoneCounter />
          <button
            className={`icon-btn ${tab === 'leaderboard' ? 'active' : ''}`}
            title="Leaderboard"
            onClick={() => setTab('leaderboard')}
          >
            <i className="fas fa-medal" />
          </button>
          {isAdmin && (
            <button
              className={`icon-btn ${tab === 'settings' ? 'active' : ''}`}
              title="Admin settings"
              onClick={() => setTab('settings')}
            >
              <i className="fas fa-cog" />
            </button>
          )}
          {user.email}
          <button className="icon-btn" onClick={signOutNow} title="Sign out">
            <i className="fas fa-sign-out-alt" />
          </button>
        </span>
      </header>
      <main className="content">
        {tab === 'settings' && isAdmin ? (
          <SettingsView userEmail={user.email ?? ''} />
        ) : tab === 'assets' ? (
          <AssetsView userEmail={user.email ?? ''} isAdmin={isAdmin} />
        ) : tab === 'sprites' ? (
          <SpriteAnimatorView />
        ) : tab === 'leaderboard' ? (
          <LeaderboardView />
        ) : (
          <TicketsView userEmail={user.email ?? ''} isAdmin={isAdmin} />
        )}
      </main>
    </div>
  );
}

function SetupNotice(): JSX.Element {
  return (
    <Centered>
      <h1>Saltshaker Games</h1>
      <p>Firebase is not configured.</p>
      <p>
        Create <code>.env.local</code> from <code>.env.local.example</code> with your{' '}
        <code>VITE_FIREBASE_*</code> keys, then restart the dev server.
      </p>
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }): JSX.Element {
  return <div className="centered">{children}</div>;
}
